<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { refDebounced, useLocalStorage } from '@vueuse/core'
import type { GlobalVariable } from 'shared/src'
import { variableTypeOptions } from 'shared/src'
import {
  useVariablesListQuery,
  useDuplicateVariableMutation,
} from '@/composables/queries/variables'
import { LIST_ACTION_ICONS, ActionButton } from '@/components/BusinessLogic/TableActions'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { useToast } from '@ai/ui-components/toast'
import CreateOrEditVariableModal from '@/components/BusinessLogic/Variables/CreateOrEditVariableModal.vue'
import { useVariableDelete } from '@/composables/variables/useVariableDelete.ts'

const { t } = useI18n()
const toast = useToast()

const variableModalRef = ref<InstanceType<typeof CreateOrEditVariableModal>>()

const searchTerm = ref('')
const searchTermDebounced = refDebounced(searchTerm, 600)

const currentPage = ref(1)
const itemsPerPage = useLocalStorage<number>(STORAGE_KEYS.VARIABLES_PAGE_SIZE, 20)

const queryParams = computed(() => ({
  q: searchTermDebounced.value,
  page: currentPage.value,
  limit: itemsPerPage.value,
}))

const { data, isLoading } = useVariablesListQuery(queryParams)
const { mutateAsync: duplicateVariableApi } = useDuplicateVariableMutation()

const variables = computed(() => data.value?.variables ?? [])
const duplicatingVariableIds = reactive(new Set<string>())
const selectedKeys = ref<(string | number)[]>([])

const paginationBinding = computed(() => ({
  currentPage: data.value?.pagination.page ?? 1,
  itemsPerPage: data.value?.pagination.pageSize ?? itemsPerPage.value,
  totalItems: data.value?.pagination.total ?? 0,
}))

const currentPageModel = computed({
  get: () => paginationBinding.value.currentPage,
  set: (v: number) => {
    currentPage.value = v
  },
})

const itemsPerPageModel = computed({
  get: () => paginationBinding.value.itemsPerPage,
  set: (v: number) => {
    itemsPerPage.value = v
    currentPage.value = 1
  },
})

const listColumns = computed(() => [
  { key: 'name', label: t('name') },
  { key: 'type', label: t('variables.type') },
  { key: 'fallbackValue', label: t('variables.fallback_value') },
  { key: 'description', label: t('description') },
])

const typeLabels = computed(() =>
  Object.fromEntries(
    variableTypeOptions.map((o) => [
      o.value,
      ['string', 'int', 'float', 'boolean'].includes(o.value)
        ? t(`variables.type_${o.value}`)
        : o.label,
    ]),
  ),
)

const {
  openDeleteModal,
  showBulkDeleteLoader,
  deleteModalItem,
  toggleDeleteModal,
  openBulkDeleteModal,
  onConfirmDelete,
} = useVariableDelete(selectedKeys)

const isVariableDuplicating = (id: string) => duplicatingVariableIds.has(id)

const duplicateVariable = async (item: GlobalVariable) => {
  if (isVariableDuplicating(item.id)) return

  duplicatingVariableIds.add(item.id)

  try {
    await duplicateVariableApi(item.id)
    toast.success(t('variables.duplicate_success'))
  } catch {
    toast.error(t('variables.create_error'), { title: t('error_occurred') })
  } finally {
    duplicatingVariableIds.delete(item.id)
  }
}

const getActions = (item: GlobalVariable) => {
  return [
    {
      label: t('edit'),
      value: 'edit',
      icon: LIST_ACTION_ICONS.edit,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      handler: () => variableModalRef.value?.openPanel(item),
      dataTestid: 'action-edit',
    },
    {
      label: t('duplicate'),
      value: 'duplicate',
      icon: LIST_ACTION_ICONS.duplicate,
      handler: () => duplicateVariable(item),
      disabled: isVariableDuplicating(item.id),
      dataTestid: 'action-duplicate',
    },
    {
      label: t('delete'),
      value: 'delete',
      icon: LIST_ACTION_ICONS.deleteDanger,
      handler: () => {
        toggleDeleteModal(item)
      },
      danger: true,
      dataTestid: 'action-delete',
    },
  ]
}

const getBulkSelectionSummary = (selectedCount: number) =>
  selectedCount === 1 ? t('one_variable_selected') : t('n_variables_selected', [selectedCount])
</script>
<template>
  <OTable
    id="variables"
    :columns="listColumns"
    :modelValue="variables"
    v-model:selectedKeys="selectedKeys"
    with-pagination
    :pagination="paginationBinding"
    v-model:currentPage="currentPageModel"
    v-model:itemsPerPage="itemsPerPageModel"
    sortable
    resizable
    :loading="isLoading || showBulkDeleteLoader"
    :state-persistence-key="STORAGE_KEYS.TABLE_STATE_BUILD_VARIABLES"
    with-bulk-actions
    row-key="id"
  >
    <template #filterActions>
      <div class="flex justify-between items-center w-full">
        <div class="flex space-x-3 items-center">
          <OInput
            v-model="searchTerm"
            iconSearch
            :placeholder="t('variables.search')"
            size="S"
            dataTestid="variables-search"
            class="w-[264px]"
          />
        </div>
        <OButton
          :label="t('create')"
          size="M"
          @click="variableModalRef?.openPanel()"
          data-testid="variables-create"
        />
      </div>
    </template>

    <template #type="{ item }">
      {{ typeLabels[item.type] ?? item.type }}
    </template>

    <template #description="{ item }">
      <OOverflowTooltip :content="item.description || '-'" :enabled="Boolean(item.description)">
        {{ item.description || '-' }}
      </OOverflowTooltip>
    </template>

    <template #bulk-selection-summary="{ selectedCount }">
      <span
        class="typography-body-2-strong text-neutral-1"
        data-testid="variables-bulk-selection-summary"
      >
        {{ getBulkSelectionSummary(selectedCount) }}
      </span>
    </template>

    <template #bulk-actions="{ selectedCount }">
      <div class="flex items-center">
        <OButton
          :label="t('delete')"
          variant="tertiary"
          color="error"
          size="S"
          :disabled="selectedCount === 0"
          dataTestid="variables-bulk-delete"
          @click="openBulkDeleteModal"
        />
      </div>
    </template>

    <template #actions="{ item }">
      <div class="flex items-center space-x-2">
        <ActionButton
          :tooltip="t('edit')"
          icon="edit"
          @click="variableModalRef?.openPanel(item)"
          data-testid="row-action-edit"
        />
        <ActionButton
          :tooltip="t('duplicate')"
          icon="duplicate"
          :disabled="isVariableDuplicating(item.id)"
          @click="duplicateVariable(item)"
          data-testid="row-action-duplicate"
        />
        <OListActions data-testid="row-action-more" small :actions="getActions(item)" />
      </div>
    </template>
  </OTable>

  <OModal
    v-if="openDeleteModal"
    v-model:open="openDeleteModal"
    :secondary-action-title="t('cancel')"
    :primary-action-title="t('delete_permanently')"
    primary-action-enabled
    secondary-action-enabled
    :secondary-action-promise="
      () => {
        openDeleteModal = false
        return true
      }
    "
    :primary-action-promise="onConfirmDelete"
    danger
    :title="t('confirm_deletion')"
  >
    <template #content>
      <i18n-t
        keypath="variables.delete_subtitle"
        tag="span"
        class="typography-body-2 text-neutral-1"
      >
        <template #name>
          <span class="typography-body-2-strong">
            {{ deleteModalItem }}
          </span>
        </template>
      </i18n-t>
    </template>
  </OModal>

  <CreateOrEditVariableModal ref="variableModalRef" />
</template>

<style scoped>
:deep([data-testid='otable-bulk-actions-row']) {
  background-color: var(--color-grayscale-100) !important;
  border-radius: var(--size-radius-m) !important;
}

:deep([data-testid='otable-bulk-actions-spacer']) {
  padding-top: calc(var(--spacing) * 4);
}
</style>
